import React, { useRef } from "react";
import { IconFileDownload, IconFileSpreadsheet } from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useStoreSettings } from "../controllers/settings.controller";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);


export default function ReportExportWrapper({
  title,
  reportType,
  dateRange,
  filters,
  tableData,
  columns,
  summaryData,
  children
}) {
  const { data: storeSettings } = useStoreSettings();
  const user = getUserDetailsInLocalStorage();
  const printRef = useRef();

  const hotelName = storeSettings?.storeName || "5-Star Hotel";
  const hotelAddress = storeSettings?.address || "Hotel Address";
  const hotelPhone = storeSettings?.phone || "Hotel Phone";
  const hotelLogo = storeSettings?.storeImage;

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    
    // Header
    if (hotelLogo) {
      // Very basic logo implementation. A real app might load the image properly.
      // doc.addImage(hotelLogo, 'PNG', 40, 40, 50, 50);
    }
    doc.setFontSize(20);
    doc.text(hotelName, 40, 50);
    doc.setFontSize(10);
    doc.text(hotelAddress, 40, 65);
    doc.text(`Phone: ${hotelPhone}`, 40, 80);

    // Title
    doc.setFontSize(16);
    doc.text(title, 40, 110);
    
    // Meta
    doc.setFontSize(10);
    doc.text(`Date Range: ${dateRange || 'All Time'}`, 40, 130);
    doc.text(`Report Type: ${reportType}`, 40, 145);
    doc.text(`Generated By: ${user?.name || 'System'}`, 40, 160);
    doc.text(`Generated On: ${new Date().toLocaleString()}`, 40, 175);

    if (filters) {
      doc.text(`Filters: ${filters}`, 40, 190);
    }

    // Table
    if (columns && tableData) {
      const head = [columns.map(c => c.header)];
      const body = tableData.map(row => columns.map(c => {
        const val = row[c.key];
        return typeof val === 'object' && val !== null ? JSON.stringify(val) : (val ?? "");
      }));
      
      autoTable(doc, {
        startY: filters ? 210 : 195,
        head: head,
        body: body,
        theme: "grid",
        headStyles: { fillColor: [40, 40, 40] }
      });
    }

    // Summary
    let finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 30 : 250;
    if (summaryData) {
      doc.setFontSize(12);
      doc.text("Summary", 40, finalY);
      finalY += 20;
      doc.setFontSize(10);
      Object.entries(summaryData).forEach(([k, v]) => {
        const valStr = typeof v === 'object' && v !== null ? JSON.stringify(v) : (v ?? "");
        doc.text(`${k}: ${valStr}`, 40, finalY);
        finalY += 15;
      });
    }

    // Signatures
    finalY += 50;
    if (finalY > 750) {
      doc.addPage();
      finalY = 50;
    }
    doc.text("_________________________", 40, finalY);
    doc.text("Prepared By", 60, finalY + 15);

    doc.text("_________________________", 350, finalY);
    doc.text("Approved By", 370, finalY + 15);

    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${pageCount} - ${hotelName}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: "center" }
      );
    }

    doc.save(`${title.replace(/\\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleExportExcel = () => {
    if (!columns || !tableData) return;

    const wb = XLSX.utils.book_new();
    
    // Meta Info Sheet
    const metaData = [
      [hotelName],
      [hotelAddress],
      [`Phone: ${hotelPhone}`],
      [],
      ["Report Title", title],
      ["Report Type", reportType],
      ["Date Range", dateRange || 'All Time'],
      ["Generated By", user?.name || 'System'],
      ["Generated On", new Date().toLocaleString()],
      ["Filters", filters || 'None']
    ];
    
    if (summaryData) {
      metaData.push([]);
      metaData.push(["Summary"]);
      Object.entries(summaryData).forEach(([k, v]) => {
        metaData.push([k, typeof v === 'object' && v !== null ? JSON.stringify(v) : (v ?? "")]);
      });
    }

    const wsMeta = XLSX.utils.aoa_to_sheet(metaData);
    XLSX.utils.book_append_sheet(wb, wsMeta, "Summary & Details");

    // Data Sheet
    const wsData = XLSX.utils.json_to_sheet(tableData.map(row => {
      let r = {};
      columns.forEach(c => {
        const val = row[c.key];
        r[c.header] = typeof val === 'object' && val !== null ? JSON.stringify(val) : (val ?? "");
      });
      return r;
    }));

    XLSX.utils.book_append_sheet(wb, wsData, "Report Data");

    XLSX.writeFile(wb, `${title.replace(/\\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Action Bar - Hidden during print */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 print:hidden">
        <div>
          <h2 className="text-2xl font-black">{title}</h2>
          <p className="text-sm text-gray-500">{dateRange} • {filters}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handlePrint} className="btn btn-sm bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 rounded-xl gap-2 hover:bg-gray-50 transition shadow-sm">
            <SafeIconPrinter size={16} stroke={iconStroke} /> Print Report
          </button>
          <button onClick={handleExportPDF} className="btn btn-sm bg-red-50 text-red-600 hover:bg-red-100 border-none rounded-xl gap-2 transition shadow-sm">
            <IconFileDownload size={16} stroke={iconStroke} /> Download PDF
          </button>
          <button onClick={handleExportExcel} className="btn btn-sm bg-green-50 text-green-700 hover:bg-green-100 border-none rounded-xl gap-2 transition shadow-sm">
            <IconFileSpreadsheet size={16} stroke={iconStroke} /> Download Excel
          </button>
        </div>
      </div>

      {/* Print Layout Container */}
      <div 
        ref={printRef}
        className="print-container bg-white dark:bg-restro-gray rounded-[2rem] p-6 shadow-sm border border-gray-100 dark:border-gray-800 print:shadow-none print:border-none print:p-0 print:m-0"
      >
        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block mb-8 text-center border-b pb-6">
          <h1 className="text-3xl font-black">{hotelName}</h1>
          <p className="text-sm text-gray-600">{hotelAddress} | Phone: {hotelPhone}</p>
          <div className="mt-6">
            <h2 className="text-2xl font-bold uppercase tracking-widest">{title}</h2>
            <p className="text-sm text-gray-500 mt-2">
              <strong>Date Range:</strong> {dateRange || 'All Time'} | <strong>Filters:</strong> {filters || 'None'}
            </p>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-4 px-10">
            <span>Generated By: {user?.name || 'System'}</span>
            <span>Printed On: {new Date().toLocaleString()}</span>
          </div>
        </div>

        {/* Content */}
        <div className="print:text-sm">
          {children}
        </div>

        {/* Print Signatures - Only visible when printing */}
        <div className="hidden print:grid grid-cols-3 gap-6 text-center mt-20 px-6 pb-10 page-break-inside-avoid avoid-break">
          <div className="flex flex-col items-center">
            <div className="w-44 border-b-2 border-black mb-2"></div>
            <p className="font-bold text-xs uppercase tracking-wider text-black">Prepared By</p>
            <p className="text-[11px] text-gray-500">Accountant / Cashier</p>
            <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-44 border-b-2 border-black mb-2"></div>
            <p className="font-bold text-xs uppercase tracking-wider text-black">Verified By</p>
            <p className="text-[11px] text-gray-500">Internal Auditor / Finance</p>
            <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-44 border-b-2 border-black mb-2"></div>
            <p className="font-bold text-xs uppercase tracking-wider text-black">Approved By</p>
            <p className="text-[11px] text-gray-500">Managing Director / Owner</p>
            <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 15mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            font-size: 10pt !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, aside, header, .appbar, .navbar, .print\\:hidden, button, input, select, a {
            display: none !important;
          }
          div[class*="pl-"], div[class*="md:pl-"], [class*="pl-"] {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
          .avoid-break, .page-break-inside-avoid {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}} />
    </div>
  );
}
